import { ImageIcon, Music, Play } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

/** A search category and how it maps to i18n + a detail route. */
export type SearchCategory = "inspection" | "resource" | "item";

/**
 * perGroup used by BOTH the dropdown and the results page so their RTK Query
 * args match (same cache key) → the two fire ONE request instead of two when you
 * press Enter mid-dropdown-search. perGroup only slices the response server-side
 * (the embed/query/enrich cost is the same), so the dropdown over-fetching is
 * free; it still displays a capped preview.
 */
export const SEARCH_PER_GROUP = 50;

export interface SearchTag {
  id: number | string;
  name: string;
  color?: string | null;
}

export interface MediaFlagsShape {
  image: boolean;
  video: boolean;
  sound: boolean;
}

/** A controlled item (finding) within an inspection that matched the query. */
export interface MatchedItem {
  itemId: number;
  title: string;
  conformity: number | null;
  severity: string | null;
  /** The user-entered finding description (shown to the user). */
  description: string | null;
  media: MediaFlagsShape;
}

export interface SearchResult {
  id: number | string;
  title: string;
  score?: number | null;
  matchedBy: string[];
  tags: SearchTag[];
  media?: MediaFlagsShape;
  // Inspection-only enrichment:
  resource?: { id: number; name: string } | null;
  flow?: { id: number; name: string } | null;
  date?: string | null;
  conformity?: number | null;
  status?: string | null;
  matchedItems?: MatchedItem[];
}

/** Readable inspection title: "Resource — Flow" (falls back to the control number). */
export function inspectionTitle(r: SearchResult): string {
  const parts = [r.resource?.name, r.flow?.name].filter(Boolean);
  return parts.length > 0 ? parts.join(" — ") : r.title;
}

/** Localised short date, or "" if absent/invalid. */
export function formatDate(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
}

export interface SearchGroup {
  category: SearchCategory;
  total: number;
  results: SearchResult[];
}

export const CATEGORY_META: Record<
  SearchCategory,
  { labelKey: string; route: (id: SearchResult["id"]) => string }
> = {
  inspection: {
    labelKey: "search.category.inspection",
    route: (id) => `/dashboard/inspections/${id}`,
  },
  resource: {
    labelKey: "search.category.resource",
    route: (id) => `/dashboard/resources/${id}`,
  },
  // Items have no dedicated detail route yet — land on the items list.
  item: {
    labelKey: "search.category.item",
    route: () => `/dashboard/items`,
  },
};

/** Placeholder mimicking a result row while a search loads. */
export function ResultSkeleton() {
  return (
    <div className="flex w-full flex-col gap-2">
      <Skeleton className="h-4 w-2/3" />
      <div className="flex gap-1">
        <Skeleton className="h-3.5 w-14 rounded-full" />
        <Skeleton className="h-3.5 w-10 rounded-full" />
      </div>
      <Skeleton className="h-3 w-1/2" />
    </div>
  );
}

/** Small inline icons showing which media kinds are present. */
export function MediaFlags({ media }: { media?: MediaFlagsShape }) {
  if (!media || (!media.image && !media.video && !media.sound)) return null;
  return (
    <span className="flex shrink-0 items-center gap-1 text-muted-foreground">
      {media.image && <ImageIcon className="size-3.5" aria-label="image" />}
      {media.video && <Play className="size-3.5" aria-label="video" />}
      {media.sound && <Music className="size-3.5" aria-label="sound" />}
    </span>
  );
}

/** A tag rendered as a coloured badge chip. */
export function TagBadge({ tag }: { tag: SearchTag }) {
  const style = tag.color
    ? {
        backgroundColor: `${tag.color}1a`,
        color: tag.color,
        borderColor: `${tag.color}55`,
      }
    : undefined;
  return (
    <Badge variant="outline" style={style} className="font-normal">
      {tag.name}
    </Badge>
  );
}
